/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// Quick Shop right-side drawer — minimal custom UI built from product JSON
(function () {
  const drawer = document.querySelector('.quickshop-drawer');
  const shade = document.querySelector('.quickshop-shade');
  if (!drawer || !shade) return;

  const body = drawer.querySelector('.quickshop-drawer__body');
  const closeBtn = drawer.querySelector('.quickshop-drawer__close');
  let abortCtrl = null;
  let state = null; // { product, selected: { OptionName: value }, currentImageIdx }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  function formatMoney(cents) {
    if (window.theme && typeof window.theme.Currency === 'object' && typeof window.theme.Currency.formatMoney === 'function') {
      return window.theme.Currency.formatMoney(cents);
    }
    return (cents / 100).toFixed(2);
  }

  function open() {
    drawer.setAttribute('aria-hidden', 'false');
    shade.setAttribute('aria-hidden', 'false');
    document.body.classList.add('quickshop-open');
  }

  function close() {
    drawer.setAttribute('aria-hidden', 'true');
    shade.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('quickshop-open');
    if (abortCtrl) { abortCtrl.abort(); abortCtrl = null; }
    state = null;
    body.innerHTML = '';
  }

  // Resolve URL to .js endpoint (handles localized paths /en/products/handle, /products/handle?variant=…)
  function toJsonUrl(url) {
    try {
      const u = new URL(url, window.location.origin);
      u.search = '';
      u.hash = '';
      if (!u.pathname.endsWith('.js')) u.pathname = u.pathname.replace(/\/$/, '') + '.js';
      return u.toString();
    } catch (e) {
      return url.split('?')[0].replace(/\/$/, '') + '.js';
    }
  }

  function findMatchingVariant() {
    if (!state) return null;
    const selected = state.selected;
    return state.product.variants.find((v) => {
      return state.product.options.every((opt, i) => {
        const name = typeof opt === 'string' ? opt : opt.name;
        return selected[name] === v.options[i];
      });
    }) || null;
  }

  // For a given option name + value, check if there's any available variant matching current selection of OTHER options
  function isValueAvailable(optionName, value) {
    const optIdx = state.product.options.findIndex((o) => (typeof o === 'string' ? o : o.name) === optionName);
    if (optIdx === -1) return true;
    return state.product.variants.some((v) => {
      if (v.options[optIdx] !== value) return false;
      if (!v.available) return false;
      // ensure other selected options match (if any)
      return state.product.options.every((opt, i) => {
        if (i === optIdx) return true;
        const name = typeof opt === 'string' ? opt : opt.name;
        const sel = state.selected[name];
        return !sel || sel === v.options[i];
      });
    });
  }

  function render() {
    const p = state.product;

    // Image: prefer image of selected variant if it has one
    const variant = findMatchingVariant();
    if (variant && variant.featured_image && variant.featured_image.src) {
      const idx = p.images.findIndex((src) => src === variant.featured_image.src || src.endsWith(variant.featured_image.src.split('/').pop().split('?')[0]));
      if (idx !== -1) state.currentImageIdx = idx;
    }
    const images = p.images.length ? p.images : [p.featured_image].filter(Boolean);
    const imgIdx = Math.max(0, Math.min(state.currentImageIdx, images.length - 1));
    const imgSrc = images[imgIdx] || '';

    const price = variant ? variant.price : p.price;
    const compareAt = variant ? variant.compare_at_price : p.compare_at_price;

    // Options HTML
    const optionsHtml = p.options.map((opt, i) => {
      const name = typeof opt === 'string' ? opt : opt.name;
      const values = (typeof opt === 'string') ? [...new Set(p.variants.map((v) => v.options[i]))] : opt.values;
      const isColor = /color|kolor|colour/i.test(name);
      const isSize = /size|rozmiar/i.test(name);
      const selected = state.selected[name];

      const valuesHtml = values.map((value) => {
        const available = isValueAvailable(name, value);
        const pressed = selected === value ? 'true' : 'false';
        if (isColor) {
          // Try to find a swatch image: check first variant matching this color and use its featured_image
          let swatchBg = '#eee';
          const matchVariant = p.variants.find((v) => v.options[i] === value);
          if (matchVariant && matchVariant.featured_image && matchVariant.featured_image.src) {
            swatchBg = 'url(' + matchVariant.featured_image.src + ')';
          } else {
            // fallback: use the value itself as a CSS color name (cream, black, etc.)
            swatchBg = value.toLowerCase();
          }
          return '<button type="button" class="qs-swatch" data-option="' + escapeHtml(name) + '" data-value="' + escapeHtml(value) + '" aria-pressed="' + pressed + '" aria-label="' + escapeHtml(value) + '" title="' + escapeHtml(value) + '" style="background:' + swatchBg + ';background-size:cover;background-position:center"' + (available ? '' : ' disabled') + '></button>';
        }
        // size or generic
        return '<button type="button" class="qs-size" data-option="' + escapeHtml(name) + '" data-value="' + escapeHtml(value) + '" aria-pressed="' + pressed + '"' + (available ? '' : ' disabled') + '>' + escapeHtml(value) + '</button>';
      }).join('');

      return ''
        + '<div class="qs-option">'
        +   '<span class="qs-option__label">' + escapeHtml(name) + (selected ? '<strong>' + escapeHtml(selected) + '</strong>' : '') + '</span>'
        +   '<div class="qs-option__values">' + valuesHtml + '</div>'
        + '</div>';
    }).join('');

    // Add to cart state
    let addLabel = 'Select options';
    let addEnabled = false;
    if (variant) {
      if (variant.available) {
        addLabel = 'Add to cart';
        addEnabled = true;
      } else {
        addLabel = 'Sold out';
      }
    } else if (p.options.length === 1 && Object.keys(state.selected).length === 0) {
      addLabel = 'Select ' + (typeof p.options[0] === 'string' ? p.options[0] : p.options[0].name);
    } else if (p.options.length > 1) {
      const missing = p.options.find((opt) => !state.selected[typeof opt === 'string' ? opt : opt.name]);
      if (missing) addLabel = 'Select ' + (typeof missing === 'string' ? missing : missing.name);
    }

    const navPrevDisabled = imgIdx === 0 ? 'disabled' : '';
    const navNextDisabled = imgIdx >= images.length - 1 ? 'disabled' : '';
    const showNav = images.length > 1;

    body.innerHTML = ''
      + '<div class="qs-gallery">'
      +   '<div class="qs-gallery__img-wrap">'
      +     (imgSrc ? '<img class="qs-gallery__img" src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(p.title) + '">' : '')
      +     (showNav ? '<button type="button" class="qs-gallery__nav qs-gallery__nav--prev" data-nav="prev" aria-label="Previous" ' + navPrevDisabled + '>&#10094;</button>' : '')
      +     (showNav ? '<button type="button" class="qs-gallery__nav qs-gallery__nav--next" data-nav="next" aria-label="Next" ' + navNextDisabled + '>&#10095;</button>' : '')
      +     (showNav ? '<div class="qs-gallery__counter">' + (imgIdx + 1) + ' / ' + images.length + '</div>' : '')
      +   '</div>'
      + '</div>'
      + '<h3 class="qs-title">' + escapeHtml(p.title) + '</h3>'
      + (p.type ? '<div class="qs-type">' + escapeHtml(p.type) + '</div>' : '')
      + '<div class="qs-price">' + formatMoney(price)
      +   (compareAt && compareAt > price ? '<span class="qs-price__compare">' + formatMoney(compareAt) + '</span>' : '')
      + '</div>'
      + optionsHtml
      + '<button type="button" class="qs-add" data-add ' + (addEnabled ? '' : 'disabled') + '>' + escapeHtml(addLabel) + '</button>'
      + '<div class="qs-error" hidden></div>';
  }

  function handleBodyClick(evt) {
    const swatch = evt.target.closest('.qs-swatch, .qs-size');
    if (swatch && !swatch.disabled) {
      const optName = swatch.dataset.option;
      const value = swatch.dataset.value;
      if (state.selected[optName] === value) {
        delete state.selected[optName];
      } else {
        state.selected[optName] = value;
      }
      render();
      return;
    }
    const nav = evt.target.closest('[data-nav]');
    if (nav && !nav.disabled) {
      const images = state.product.images.length ? state.product.images : [state.product.featured_image].filter(Boolean);
      if (nav.dataset.nav === 'prev') state.currentImageIdx = Math.max(0, state.currentImageIdx - 1);
      else state.currentImageIdx = Math.min(images.length - 1, state.currentImageIdx + 1);
      render();
      return;
    }
    const addBtn = evt.target.closest('[data-add]');
    if (addBtn && !addBtn.disabled) {
      addToCart(addBtn);
    }
  }

  function addToCart(btn) {
    const variant = findMatchingVariant();
    if (!variant) return;
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Adding…';
    const errEl = body.querySelector('.qs-error');
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data: data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data && (data.description || data.message) || 'Could not add to cart');
        document.dispatchEvent(new CustomEvent('on:cart:add', { detail: { variantId: variant.id }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        close();
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
      })
      .catch((err) => {
        if (errEl) { errEl.textContent = err.message; errEl.hidden = false; }
        btn.disabled = false;
        btn.textContent = original;
      });
  }

  function loadProduct(productUrl) {
    body.innerHTML = '<div class="quickshop-drawer__loading"><div class="loading-spinner"></div></div>';
    open();

    if (abortCtrl) abortCtrl.abort();
    abortCtrl = new AbortController();

    fetch(toJsonUrl(productUrl), { signal: abortCtrl.signal, headers: { 'Accept': 'application/json' } })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then((product) => {
        state = { product: product, selected: {}, currentImageIdx: 0 };
        // Auto-select: single-value options + always pre-select first color value
        product.options.forEach((opt, i) => {
          const name = typeof opt === 'string' ? opt : opt.name;
          const values = (typeof opt === 'string') ? [...new Set(product.variants.map((v) => v.options[i]))] : opt.values;
          const isColor = /color|kolor|colour/i.test(name);
          if (values.length > 0 && (values.length === 1 || isColor)) state.selected[name] = values[0];
        });
        render();
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        body.innerHTML = '<p style="padding:30px;text-align:center;color:#888">Could not load product.</p>';
        console.warn(err);
      });
  }

  // Click on + button on product cards
  document.addEventListener('click', function (evt) {
    const trigger = evt.target.closest('.nb-card-qb');
    if (!trigger) return;
    evt.preventDefault();
    evt.stopPropagation();
    if (trigger.href) loadProduct(trigger.href);
  });

  body.addEventListener('click', handleBodyClick);
  closeBtn.addEventListener('click', close);
  shade.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') close();
  });
})();

